import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';
import { GalleryItem } from '../../../types';
import SelectGalleryImageDialog from './SelectGalleryImageDialog';


interface AdminGalleryCardProps {
  item: GalleryItem;
  onDelete: (item: GalleryItem) => void;
  onEdit: (item: GalleryItem) => void;
  onCloseEdit: () => void;
  onSave: (file: File | undefined, title?: string, description?: string) => void;
  isEditing?: boolean;
}

const AdminGalleryCard: React.FC<AdminGalleryCardProps> = ({
  item,
  onDelete,
  onEdit,
  onCloseEdit,
  onSave,
  isEditing = false,
}) => {
  const [downloadURL, setDownloadURL] = useState<string | null>(item.imageURL || null);

  useEffect(() => {
    if (item.imageURL) {
      setDownloadURL(item.imageURL);
      return;
    }
    if (!item.imagePath) {
      setDownloadURL(null);
      return;
    }
    const fetchURL = async () => {
      try {
        const url = await getDownloadURL(ref(storage, item.imagePath));
        setDownloadURL(url);
      } catch (error) {
        console.error("Failed to fetch image URL:", error);
        setDownloadURL(null);
      }
    };

    fetchURL();
  }, [item.imagePath, item.imageURL]);

  return (
    <Card sx={{ mb: 2, borderRadius: '12px', boxShadow: 1, position: 'relative' }}>
      <CardContent sx={{ p: 0, pb: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" noWrap>
              {isEditing ? 'Edit Gallery Image' : item.title || 'Untitled'}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              {isEditing ? (
                <Tooltip title="Cancel">
                  <IconButton onClick={onCloseEdit} color="primary">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Edit">
                  <IconButton onClick={() => onEdit(item)} color="primary">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete">
                <IconButton onClick={() => onDelete(item)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Collapse
          in={!isEditing}
          sx={{ px: 2, pb: 2 }}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 0, exit: 0 }}
        >
          <Box display="flex" justifyContent="center" mb={2}>
            {downloadURL ? (
              <Box
                component="img"
                src={downloadURL}
                alt={item.title || 'Gallery Image'}
                sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 2 }}
              />
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>

          {item.description ? (
            <Typography variant="body2" color="textSecondary">
              {item.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary" fontStyle="italic">
              No description provided.
            </Typography>
          )}
        </Collapse>

        <Collapse in={isEditing} sx={{ transformOrigin: 'top center' }} mountOnEnter unmountOnExit>
          <Divider sx={{ mb: 1 }} />
          <SelectGalleryImageDialog
            open={isEditing}
            onClose={onCloseEdit}
            onSave={onSave}
            initialItem={item}
            mode="inline"
            inlineVariant="embedded"
          />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AdminGalleryCard;
